import { ErrorMessage } from '@hookform/error-message';
import { InfiniteQueryObserverResult } from '@tanstack/react-query';
import classnames from 'classnames';
import clsx from 'clsx';
import { useTranslation } from 'next-i18next';
import React, { CSSProperties, useCallback, useMemo, useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import Select, {
  CSSObjectWithLabel,
  GroupBase,
  MenuListProps,
  Props,
  SelectInstance,
  StylesConfig,
} from 'react-select';
import { components } from 'react-select';
import { useAsyncPaginate, wrapMenuList } from 'react-select-async-paginate';

import styles from './SelectField.module.css';

import CreateOptionButton from '@/shared/components/atoms/SelectField/CreateOptionButton';
import { ISelectFieldProps } from '@/shared/components/atoms/SelectField/ISelectField';
import { Typography } from '@/shared/components/atoms/Typography';
import { IGeneral } from '@/shared/types';

/**
 * Infinite scroll select field
 * @param props Accepts the normal select props
 * @param fetchNextPage used to fetch more results, returned from react-query
 */
export default function InfiniteSelectField<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
>(
  props: Props<Option, IsMulti, Group> &
    ISelectFieldProps<Option, IsMulti, Group> & {
      fetchNextPage: () => Promise<
        InfiniteQueryObserverResult<
          IGeneral.ISuccessResponseDataSchema<unknown>
        >
      >;
    }
) {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const { t } = useTranslation('common');
  const customControlStyles: CSSProperties = {
    // color: textColor,
    borderColor: errors[props?.id] ? '#CC4949' : props?.borderColor,
    fontSize: props?.fontSize,
    paddingLeft: props?.px,
    paddingRight: props?.px,
    paddingTop: props?.py,
    paddingBottom: props?.py,
    borderRadius: props?.borderRadius || '0.5rem',
    backgroundColor: props?.backgroundColor,
    outlineColor: 'transparent',
  };
  const selectStyles:
    | StylesConfig<Option, (IsMulti & false) | (IsMulti & true), Group>
    | undefined = {
    option: (provided, state) => {
      const temp: CSSObjectWithLabel = {
        ...provided,

        zIndex: 99,
      };

      if (state.isSelected) {
        temp.backgroundColor = '#C8DDF5';
      }

      return temp;
    },

    control: (provided) => {
      return {
        ...provided,

        ...customControlStyles,
      };
    },

    menuPortal: (provided) => {
      return {
        ...provided,
        zIndex: 999999,
      };
    },
    multiValue: (provided) => {
      return {
        ...provided,
        borderRadius: '8px',
      };
    },
    multiValueRemove: (provided) => {
      return {
        ...provided,
        borderRadius: '8px',
      };
    },
  };

  const labelClasses = classnames(styles['label'], {
    [styles['label-disabled']]: props?.isDisabled,
  });

  // used to get new options that will be added to the previous ones
  const loadOptions = async () => {
    const allOptions = await props.fetchNextPage();
    const lastPage = allOptions.data?.pages
      ? allOptions.data.pages[allOptions.data.pages.length - 1]
      : undefined;
    const newOptions = lastPage?.items || [];

    return {
      options: newOptions,
      hasMore: lastPage ? lastPage.currentPage < lastPage.totalPages : false,
    };
  };

  const asyncPaginateProps = useAsyncPaginate({
    loadOptions,
    options: props?.list,
    filterOption: (option, search) =>
      option?.label?.toLocaleLowerCase().includes(search.toLocaleLowerCase()),
  });

  const selectRef = useRef<SelectInstance<Option, IsMulti, Group> | null>(null);

  const createMenuList = useCallback(
    ({ children, ...menuListProps }: MenuListProps<Option, IsMulti, Group>) => (
      <components.MenuList {...menuListProps}>
        {props?.customMenuList ? (
          props.customMenuList({ children, ...menuListProps }, selectRef)
        ) : props?.createOption ? (
          <>
            <CreateOptionButton
              title={props.createOption.title}
              subTitle={props.createOption.subTitle}
              onClick={() => {
                props.createOption?.onClick();
                // close menu after opening modal
                selectRef.current?.blur();
              }}
            />
            {children}
          </>
        ) : (
          children
        )}
      </components.MenuList>
    ),
    [props]
  );

  // useMemo prevent scrollbar from going to the top on fetch
  // wrap MenuList to get scroll position
  const MenuList = useMemo<React.FC<MenuListProps<Option, IsMulti, Group>>>(
    () => wrapMenuList(createMenuList),
    [createMenuList]
  );

  const options: Option[] =
    props?.options?.flatMap((group: GroupBase<Option>) => group.options) ?? [];

  return (
    <div className={`relative  flex ${props?.width} min-w-[27%] flex-col`}>
      {props?.label ? (
        <label htmlFor={props?.id} className={labelClasses}>
          <Typography variant='body1' weight='regular'>
            {props?.label}{' '}
            {props?.required && <span className={styles['required']}>*</span>}
          </Typography>
        </label>
      ) : null}
      <div className='relative'>
        <Controller
          name={props?.id}
          control={control}
          defaultValue={props?.defaultValue}
          render={({ field: { onChange, value, ...rest } }) => (
            <Select
              {...asyncPaginateProps}
              {...rest}
              {...props}
              ref={selectRef}
              className={clsx(
                'rounded-xl',
                props?.isDisabled && styles['disabled']
              )}
              // serialize value to be like option
              value={
                props?.value ||
                (props.isGroup
                  ? options.filter((item: Option) =>
                      props.isMulti
                        ? value?.includes(props.getValue(item))
                        : value === props.getValue(item)
                    )
                  : props?.list?.filter((item: Option) =>
                      props.isMulti
                        ? value?.includes(props.getValue(item))
                        : value === props.getValue(item)
                    ))
              }
              // if there are custom component for rendering options
              components={
                props.customOption
                  ? {
                      Option: (optopnProps) => (
                        <components.Option {...optopnProps}>
                          {props?.customOption?.(optopnProps)}
                        </components.Option>
                      ),
                      MenuList,
                    }
                  : { MenuList }
              }
              options={props.options ?? props?.list}
              getOptionLabel={props?.getLabel}
              getOptionValue={props?.getValue}
              // only send value to onchange
              onChange={(e) => {
                if (props?.isMulti) {
                  // assert 'e' is an array
                  onChange(
                    (e as Option[]).map((item: Option) => props?.getValue(item))
                  );
                } else {
                  // assert 'e' is a single value
                  onChange(props?.getValue(e as Option));
                }
              }}
              styles={selectStyles}
              noOptionsMessage={
                props?.noOptionsMessage
                  ? props?.noOptionsMessage
                  : () => t('no-options-message')
              }
              menuPortalTarget={document.body}
            />
          )}
        />
      </div>
      <ErrorMessage
        errors={errors}
        name={props?.errorMessageId ?? props?.id}
        render={({ message }: { message: string }) => (
          <Typography
            className='mt-8 text-error-default'
            variant='body2'
            weight='regular'
          >
            {message}
          </Typography>
        )}
      />
      {props?.isDisabled && props?.disabledMessage && (
        <Typography variant='subtitle2' className='mt-4 opacity-4'>
          {props.disabledMessage}
        </Typography>
      )}
    </div>
  );
}

InfiniteSelectField.defaultProps = {
  py: '2px',
  px: '',
  textColor: 'text-text-default',
  fontSize: 'text-base',
  borderColor: 'border-background-disable',
  placeholderColor: 'text-text-secondary',
};
